import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Plan {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  plan_name: string;
  plan_code: string;
  plan_type: string;
  plan_cost: number;
  duration_days: number;
  description: string;
  features: string;
  is_active: boolean;
  max_members: number;
  trial_days: number;
  sort_order: number;
}

export interface Subscription {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  customer_id: number;
  plan_id: number;
  plan: Plan;
  status: string;
  start_date: string;
  end_date: string;
  renewal_date: string;
  cancelled_at: string;
  paused_at: string;
  payment_id: string;
  auto_renew: boolean;
  cancel_reason: string;
  is_trial: boolean;
}

interface SubscriptionsState {
  plans: Plan[];
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
}

const initialState: SubscriptionsState = {
  plans: [],
  subscriptions: [],
  loading: false,
  error: null,
};

// Plans
export const fetchPlans = createAsyncThunk(
  'subscriptions/fetchPlans',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.sms}/api/v1/plans`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch plans');
    }
  }
);

export const createPlan = createAsyncThunk(
  'subscriptions/createPlan',
  async (data: Partial<Plan>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.sms}/api/v1/plans`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create plan');
    }
  }
);

export const updatePlan = createAsyncThunk(
  'subscriptions/updatePlan',
  async ({ id, data }: { id: number; data: Partial<Plan> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.sms}/api/v1/plans/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update plan');
    }
  }
);

export const deletePlan = createAsyncThunk(
  'subscriptions/deletePlan',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.sms}/api/v1/plans/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete plan');
    }
  }
);

// Subscriptions
export const fetchSubscriptions = createAsyncThunk(
  'subscriptions/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.sms}/api/v1/subscriptions`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch subscriptions');
    }
  }
);

export const expireSubscriptions = createAsyncThunk(
  'subscriptions/expire',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.sms}/api/v1/subscriptions/expire`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to expire subscriptions');
    }
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearSubscriptionsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // Plans
      .addCase(fetchPlans.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPlans.fulfilled, (state, action) => { state.loading = false; state.plans = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchPlans.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createPlan.fulfilled, (state, action) => {
        const p = action.payload?.data || action.payload;
        if (p) state.plans.push(p);
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.plans.findIndex(p => p.ID === updated.ID);
          if (idx !== -1) state.plans[idx] = updated;
        }
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.plans = state.plans.filter(p => p.ID !== action.payload);
      })
      // Subscriptions
      .addCase(fetchSubscriptions.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => { state.loading = false; state.subscriptions = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchSubscriptions.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { clearSubscriptionsError } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
